import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ToastProvider } from './components/ToastContext';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';

import ProjectsList from './pages/ProjectsList';
import ProjectBoard from './pages/ProjectBoard';
import LayoutsList from './pages/LayoutsList';
import LayoutEditor from './pages/LayoutEditor';
import UsersList from './pages/UsersList';
import RecipientsManager from './pages/RecipientsManager';

import NavBar from './components/NavBar';
import Footer from './components/Footer';
import Spinner from './components/Spinner';

import ProjectEditor from './pages/ProjectEditor';
import LoanEligibility from './pages/LoanEligibility';
import LoanAdmin from './pages/LoanAdmin';
import Dashboard from './pages/Dashboard';

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="card">
        <Spinner text="Checking session..." />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="card">
        <Spinner text="Checking session..." />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  return children;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="card">
        <Spinner text="Checking session..." />
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

/**
 * Root redirect:
 * - logged-in users → /dashboard
 * - logged-out users → /login
 */
function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="card">
        <Spinner text="Checking session..." />
      </div>
    );
  }

  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <NavBar />

          <div style={{ padding: '1em', flex: 1 }}>
            <Routes>
              {/* Public auth pages */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Root */}
              <Route path="/" element={<RootRedirect />} />

              {/* Logged-in self-service */}
              <Route
                path="/change-password"
                element={
                  <PrivateRoute>
                    <ChangePassword />
                  </PrivateRoute>
                }
              />

              {/* Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />

              {/* Main app */}
              <Route
                path="/projects"
                element={
                  <PrivateRoute>
                    <ProjectsList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/project/new"
                element={
                  <AdminRoute>
                    <ProjectEditor />
                  </AdminRoute>
                }
              />

              <Route
                path="/project/:id"
                element={
                  <PrivateRoute>
                    <ProjectBoard />
                  </PrivateRoute>
                }
              />

              <Route
                path="/layouts"
                element={
                  <AdminRoute>
                    <LayoutsList />
                  </AdminRoute>
                }
              />

              {/* You already have layouts/:mode/:id? and layout-editor routes in your repo.
                  Keep whichever ones you actually use; these are safe and match your previous wiring. */}
              <Route
                path="/layouts/:mode/:id?"
                element={
                  <PrivateRoute>
                    <LayoutEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/layout-editor/:mode"
                element={
                  <PrivateRoute>
                    <LayoutEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/layout-editor/:mode/:id"
                element={
                  <PrivateRoute>
                    <LayoutEditor />
                  </PrivateRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <AdminRoute>
                    <UsersList />
                  </AdminRoute>
                }
              />

              <Route
                path="/recipients"
                element={
                  <SuperAdminRoute>
                    <RecipientsManager />
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/loan-eligibility"
                element={
                  <PrivateRoute>
                    <LoanEligibility />
                  </PrivateRoute>
                }
              />

              <Route
                path="/loan-admin"
                element={
                  <SuperAdminRoute>
                    <LoanAdmin />
                  </SuperAdminRoute>
                }
              />

              {/* Catch-all fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>

          <Footer />
        </div>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
